import * as api from "./client";

export type CompanyInfo = {
  /** Address-book UUID */
  id: string;
  /** Legacy numeric company id */
  companyId: number;
  name: string;
};

export type Firm = {
  /** Address-book UUID — use this when wiring filters into profiles-api list endpoints. */
  id: string;
  /** Legacy numeric company id. Retained for callers that still need it. */
  companyId: number;
  name: string;
  crmEntityNumber: string | null;
  firmRef: string | null;
  slug: string | null;
};

export type FirmsListResponse = {
  companies: Firm[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
};

export type ApprovalFirm = {
  firmRef: string;
  firmName: string | null;
};

export type ApprovalFirmsResponse = {
  firms: ApprovalFirm[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  requestId?: string;
};

export type ApprovalFirmsFilters = {
  publicationId?: number;
  practiceAreaId?: number;
  tableId?: number;
  edition?: string;
  lang?: string;
  status?: string;
  contentType?: string;
  limit?: number;
  offset?: number;
};

type AddressBookCompany = {
  id: string;
  companyId: number;
  name: string;
  crmEntityNumber: string | null;
  firmRef: string | null;
  slug: string | null;
};

function toFirm(company: AddressBookCompany): Firm {
  return {
    id: company.id,
    companyId: company.companyId,
    name: company.name,
    crmEntityNumber: company.crmEntityNumber,
    firmRef: company.firmRef,
    slug: company.slug,
  };
}

export const firms = {
  getAll: async (filters?: {
    limit?: number;
    offset?: number;
    search?: string;
    businessType?: string;
  }): Promise<FirmsListResponse> => {
    const params = new URLSearchParams();
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());
    if (filters?.search) params.append("name", filters.search);
    if (filters?.businessType) params.append("businessType", filters.businessType);

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{
      companies: AddressBookCompany[];
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    }>(`/v1/address-book/companies${query}`);

    return {
      companies: response.companies.map(toFirm),
      pagination: response.pagination,
    };
  },

  /**
   * Batch lookup companies by their legacy numeric `companyId`.
   * Note: the address-book batch endpoint only accepts numeric ids. For UUID
   * batching use `getBatchByUuids`.
   */
  getBatchByIds: async (ids: number[]): Promise<CompanyInfo[]> => {
    if (ids.length === 0) return [];
    const response = await api.post<{
      companies: Array<{ id: number; name: string }>;
    }>("/v1/address-book/companies/batch", { ids });
    // The batch endpoint returns the legacy numeric id as `id`. Map it to
    // `companyId` and leave UUID unknown — callers who need UUID should use
    // `getBatchByUuids` instead.
    return response.companies.map(c => ({
      id: "",
      companyId: c.id,
      name: c.name,
    }));
  },

  /**
   * Batch lookup companies by their address-book UUID.
   * Uses the list endpoint with the `ids` query (which accepts UUIDs); we
   * page through results in case the caller passes more than the server's
   * default page size.
   */
  getBatchByUuids: async (uuids: string[]): Promise<CompanyInfo[]> => {
    if (uuids.length === 0) return [];
    const deduped = [...new Set(uuids)].filter(Boolean);
    if (deduped.length === 0) return [];

    const params = new URLSearchParams();
    params.append("ids", deduped.join(","));
    params.append("limit", String(Math.max(deduped.length, 100)));

    const response = await api.get<{
      companies: AddressBookCompany[];
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    }>(`/v1/address-book/companies?${params.toString()}`);

    return response.companies.map(c => ({
      id: c.id,
      companyId: c.companyId,
      name: c.name,
    }));
  },

  getBySlug: async (slug: string, limit = 50): Promise<FirmsListResponse> => {
    const params = new URLSearchParams();
    params.append("slug", slug);
    params.append("limit", limit.toString());
    const query = `?${params.toString()}`;
    const response = await api.get<{
      companies: AddressBookCompany[];
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    }>(`/v1/address-book/companies${query}`);
    return {
      companies: response.companies.map(toFirm),
      pagination: response.pagination,
    };
  },

  /**
   * Get a single firm by legacy numeric `companyId`.
   */
  getById: async (id: number): Promise<Firm | null> => {
    try {
      const response = await api.get<{
        company: AddressBookCompany;
      }>(`/v1/address-book/companies/by-company-id/${id}`);

      return toFirm(response.company);
    } catch (err) {
      // Treat 404 as "not found" (caller decides what to render); re-throw
      // everything else so genuine API failures surface instead of being
      // masked as a missing record.
      if ((err as { status?: number })?.status === 404) return null;
      throw err;
    }
  },

  /**
   * Get a single firm by address-book UUID.
   * Hits `GET /v1/address-book/companies/{uuid}` which proxies to
   * `address-book-api`'s `/api/v1/companies/{id}`.
   */
  getByUuid: async (uuid: string): Promise<Firm | null> => {
    if (!uuid) return null;
    try {
      const response = await api.get<{
        company: AddressBookCompany;
      }>(`/v1/address-book/companies/${encodeURIComponent(uuid)}`);
      return toFirm(response.company);
    } catch (err) {
      if ((err as { status?: number })?.status === 404) return null;
      throw err;
    }
  },

  /**
   * Get distinct firms from approval requests
   * Supports filtering by publicationId, practiceAreaId, tableId, edition, lang, status, contentType
   */
  getApprovalFirms: async (filters?: ApprovalFirmsFilters): Promise<ApprovalFirmsResponse> => {
    const params = new URLSearchParams();
    if (filters?.publicationId !== undefined)
      params.append("publicationId", filters.publicationId.toString());
    if (filters?.practiceAreaId !== undefined)
      params.append("practiceAreaId", filters.practiceAreaId.toString());
    if (filters?.tableId !== undefined) params.append("tableId", filters.tableId.toString());
    if (filters?.edition) params.append("edition", filters.edition);
    if (filters?.lang) params.append("lang", filters.lang);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.contentType) params.append("contentType", filters.contentType);
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<ApprovalFirmsResponse>(`/v1/approval/approval-requests/firms${query}`);
  },

  getAllApprovalFirms: async (
    filters?: Omit<ApprovalFirmsFilters, "limit" | "offset">,
  ): Promise<ApprovalFirm[]> => {
    const BATCH_SIZE = 100;
    const allFirms: ApprovalFirm[] = [];
    let offset = 0;

    while (true) {
      const resp = await firms.getApprovalFirms({ ...filters, limit: BATCH_SIZE, offset });
      allFirms.push(...resp.firms);
      offset += BATCH_SIZE;
      if (resp.firms.length < BATCH_SIZE) break;
    }

    return allFirms;
  },

  getAllBySearch: async (search: string): Promise<Firm[]> => {
    const BATCH_SIZE = 100;
    const allFirms: Firm[] = [];
    let offset = 0;

    while (true) {
      const resp = await firms.getAll({ search, limit: BATCH_SIZE, offset });
      allFirms.push(...resp.companies);
      offset += BATCH_SIZE;
      if (!resp.pagination.hasMore) break;
    }

    return allFirms;
  },
};
